// The actions a queued entry's row offers (Retry, Discard) and the device-copy correction
// a discard makes. Split from change-queue-replay.ts to keep that file under its size limit.
import {
  isQueuedLotCreate,
  lotIdOfGroupKey,
  readQueuedEntry,
} from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import { dependentsOf } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-plan';
import type { OfflineMediaStaging, OfflineStore, OutboxEntry } from '../../gateways/native-device';
import { requestFullDownload } from '../offline-sync/apply-auction-delta';
import { stagedFilesOf } from './change-queue-uploads';
import type { ChangeQueueActionResult } from './change-queue-replay';
import { dropCopyLot } from './lot-state-actions';

export async function retryEntry(store: OfflineStore, id: string): Promise<ChangeQueueActionResult> {
  const entries = await store.outbox.list();
  const entry = entries.find((e) => e.id === id);
  if (!entry) return { success: false, code: 'NOT_FOUND' };
  if (entry.status !== 'failed') return { success: false, code: 'NOT_FAILED' };
  await store.outbox.update({ id: entry.id, status: 'pending', error: null, attempts: 0 });
  return { success: true };
}

export async function discardEntry(deps: { store: OfflineStore; media: OfflineMediaStaging | null }, id: string): Promise<ChangeQueueActionResult> {
  const { store, media } = deps;
  const entries = await store.outbox.list();
  const entry = entries.find((e) => e.id === id);
  if (!entry) return { success: false, code: 'NOT_FOUND' };
  if (entry.status !== 'failed' && entry.status !== 'refused') return { success: false, code: 'NOT_FAILED' };
  const doomed = [entry, ...dependentsOf(entries, entry.id)];
  for (const e of doomed) {
    const read = readQueuedEntry(e);
    if (read.ok) for (const id of stagedFilesOf(read.payload)) await media?.delete(id);
    await store.outbox.remove(e.id);
  }
  // A change made to a relist's copy lot waits on the relist, so it can only go with it.
  if (isQueuedLotCreate(entry)) await dropCopyLot(store, media, entry.id);
  await correctDeviceCopy(store, entry);
  return { success: true };
}

/**
 * The device still shows a discarded change (offline writes apply to the cached lot, BL-2431).
 * A discarded new lot is simply deleted; any other change re-downloads its lot's auction on the
 * next sync (plan Q5, Michael Lawrie 2026-10-10).
 */
async function correctDeviceCopy(store: OfflineStore, entry: OutboxEntry): Promise<void> {
  if (isQueuedLotCreate(entry)) {
    await store.records.delete({ collection: 'lots', key: entry.id });
    return;
  }
  const lotId = lotIdOfGroupKey(entry.groupKey);
  if (lotId == null) return;
  const lot = await store.records.get({ collection: 'lots', key: lotId });
  const auctionId = lot?.detail.auctionId;
  if (auctionId) await requestFullDownload({ store, auctionId });
  // BL-2448: a discarded move already shows the lot in its new auction, so the one it left is
  // re-downloaded too, or the lot would be missing from it on the device.
  const read = readQueuedEntry(entry);
  const left = read.ok ? read.payload.leftAuctionId : null;
  if (left != null && left !== auctionId) await requestFullDownload({ store, auctionId: left });
}
